/**
 * C2 — the extended-visibility row inside one portfolio card.
 *
 * The consent asymmetry, stated verbatim and identically everywhere it appears:
 * "your switch alone; a portfolio can turn it off, never on". It mirrors the
 * deployed rule in sponsorship/handler.py: the inventor is the ONLY caller who
 * may turn it on, and only after typing a confirmation; the organisation may
 * only turn it off.
 *
 * Story #5648 build v2 moved it INSIDE the card. It used to be a free-standing
 * list that fetched its own data and printed one line per organisation, which
 * separated a consent switch from the relationship it belongs to — the reader
 * had to match name to name to know what they were consenting to. It is now
 * presentation only: one row, one organisation, whose card it sits in. Bug
 * #5829 (one shared boolean displayed against every organisation's name) cannot
 * recur by construction, because there is no shared state left to get wrong.
 */
import { useState } from 'react'

export interface ExtendedVisibilityRowProps {
  organisation: string
  isOn: boolean
  /** Turns it ON. There is no OFF here: an inventor turns it off through the
   *  same route, but the figure's card shows only the enable affordance. */
  onEnable: () => Promise<void>
}

export default function ExtendedVisibilityRow({ organisation, isOn, onEnable }: ExtendedVisibilityRowProps) {
  const [busy, setBusy] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [locallyOn, setLocallyOn] = useState<boolean | null>(null)
  const on = locallyOn ?? isOn

  const enable = async () => {
    setBusy(true)
    setErrorMessage(null)
    try {
      await onEnable()
      setLocallyOn(true)
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Could not turn extended visibility on.'
      setErrorMessage(message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-part="ExtendedVisibilityRow" className="mt-2 border-t border-slate-100 pt-2">
      <div className="flex items-center gap-2.5 text-[11.5px] leading-relaxed text-slate-500">
        <span
          aria-hidden
          className={`inline-block h-[18px] w-[32px] shrink-0 rounded-full ${on ? 'bg-iflow-500' : 'bg-slate-300'} relative`}
        >
          <span className={`absolute top-[2px] ${on ? 'right-[2px]' : 'left-[2px]'} h-[14px] w-[14px] rounded-full bg-white shadow`} />
        </span>
        <span>
          Extended visibility: <b className="text-slate-700">{on ? 'on' : 'off'}</b> — your
          switch alone; a portfolio can turn it off, never on
        </span>
        {!on && (
          <button
            type="button"
            onClick={enable}
            disabled={busy}
            title={`Turning this on for ${organisation} asks you to type “I understand”`}
            className="ml-auto shrink-0 whitespace-nowrap text-iflow-700 underline disabled:opacity-50"
          >
            {busy ? 'Turning on…' : 'Turn on (type “I understand”)'}
          </button>
        )}
      </div>
      {errorMessage && <p className="mt-1 text-[11.5px] text-rose-600">{errorMessage}</p>}
    </div>
  )
}
